import { useEffect, useState } from "react";
import PageTitle from "../../components/common/PageTitle/PageTitle";
import { useTranslation } from "react-i18next";
import { Box, Tab, Tabs, Typography } from "@mui/material";
import CompanyInfoTable from "./CompanyInfoTable";
import { companyManagementApi } from "../../api/companyManagementApi";
import { CompanyType } from "../../types/companyManagementTypes";
import {
  CompanyManagementMainContentStyles,
  SEPAInstructionsStyles,
} from "./CompanyManagement.styles";
import { useAppDispatch } from "../../hooks/StoreHooks";
import { fetchSubCompaniesThunk } from "../../store/thunk/subCompaniesThunk";
import { useSubCompaniesData } from "../../store/selectors/subCompaniesSelector";
import AddIcon from "@mui/icons-material/Add";
import AddOrEditSubCompanyDialog from "./AddOrEditSubCompanyDialog";
import { usePermissionChecker } from "../../hooks/usePermissionChecker";
import { PermissionEntityType } from "../../types/permissionType";

const CompanyManagement = () => {
  const { t } = useTranslation();
  const dispatch = useAppDispatch();
  const [tableData, setTableData] = useState<CompanyType | null>(
    null
  );
  const [error, setError] = useState<string>("");
  const [selectedTab, setSelectedTab] = useState(0);
  const [addDialogOpen, setAddDialogOpen] = useState(false);
  const [editDialogOpen, setEditDialogOpen] = useState(false);
  const [editInitialData, setEditInitialData] = useState<Partial<CompanyType> | null>(null);

  const subCompanies = useSubCompaniesData();
  const { userHasPermission } = usePermissionChecker();
  const canCreateSubCompany = userHasPermission(PermissionEntityType.SubCompanies, "Create");
  const visibleSubCompanies = subCompanies.filter(sub =>
    userHasPermission(PermissionEntityType.SubCompanies, "View") ||
    userHasPermission(PermissionEntityType.SubCompanies, "View", sub.id)
  );

  const handleTabChange = (_: React.SyntheticEvent, newValue: number) => {
    if (newValue === (subCompanies.length + 1)) {
      setAddDialogOpen(true);
    } else {
      setSelectedTab(newValue);
    }
  };

  useEffect(() => {
    dispatch(fetchSubCompaniesThunk()).unwrap()
    const fetchCompany = async () => {
      try {
        const company =
          (await companyManagementApi.getCompany()) as CompanyType;
        if (!company)
          throw new Error("Something whent wrong, please try again");
        else {
          setTableData(company);
        }
      } catch (error) {
        setError((error as Error).message);
      }
    };
    fetchCompany();
  }, [dispatch]);

  const handleEditSubCompany = (company: CompanyType) => {
    setEditInitialData({
      id: company.id,
      name: company.name,
      phoneNumber: company.phoneNumber,
      taxId: company.taxId,
      invoiceEmail: company.invoiceEmail,
      address: {
        street: company.address.street,
        number: company.address.number,
        extraAddressLine: company.address.extraAddressLine,
        postalCode: company.address.postalCode,
        town: company.address.town,
        country: company.address.country,
        canBeProcessed: company.address.canBeProcessed,
      },
    });
    setEditDialogOpen(true);
  };

  const renderTabs = () => (
    <Tabs value={selectedTab} onChange={handleTabChange} sx={{ mb: 3 }}>
      {tableData && <Tab label={tableData.name} />}
      {visibleSubCompanies.map(sub => (
        <Tab key={sub.id} label={sub.name} />
      ))}
      {canCreateSubCompany && <Tab icon={<AddIcon />} />}
    </Tabs>
  );

  if (tableData) {
    return (
      <>
        <PageTitle
          title={t("companyManagementPage.title")}
        />
        {renderTabs()}

        <CompanyManagementMainContentStyles>
          {selectedTab === 0 && tableData && (
            <>
              <CompanyInfoTable
                displayCanNotBeProcessed
                companyTableInfo={tableData}
                invoiceEmail={tableData.invoiceEmail}
                title={t("companyManagementPage.companyInfoTable.billAddress")}
              />
              <CompanyInfoTable
                displayCanNotBeProcessed={false}
                companyTableInfo={tableData}
                title={t("companyManagementPage.companyInfoTable.deliveryAddress")}
              />
            </>
          )}

          {selectedTab > 0 && selectedTab <= subCompanies.length && (
            <>
              <CompanyInfoTable
                displayCanNotBeProcessed
                companyTableInfo={subCompanies[selectedTab - 1]}
                invoiceEmail={subCompanies[selectedTab - 1].invoiceEmail}
                title={t("companyManagementPage.companyInfoTable.billAddress")}
                isSubcompany
                onEdit={handleEditSubCompany}
              />
              <CompanyInfoTable
                displayCanNotBeProcessed={false}
                companyTableInfo={subCompanies[selectedTab - 1]}
                title={t("companyManagementPage.companyInfoTable.deliveryAddress")}
              />
            </>
          )}

          {selectedTab === 0 && (
            <SEPAInstructionsStyles>
              <Typography>{t("companyManagementPage.companyInfoTable.sepaLinkText")}</Typography>
              <Typography>
                {t("companyManagementPage.companyInfoTable.sepaText1")}{" "}
                <a
                  href="https://bonisoft.de/anleitung-sepa-mandat/"
                  target="_blank"
                  rel="noreferrer">
                  {t("companyManagementPage.companyInfoTable.sepaLinkText")}
                </a>{" "}
                {t("companyManagementPage.companyInfoTable.sepaText2")}
              </Typography>
            </SEPAInstructionsStyles>
          )}

          <AddOrEditSubCompanyDialog
            open={addDialogOpen}
            onClose={() => setAddDialogOpen(false)}
            onSuccess={(newSub) => {
              const newIndex = subCompanies.findIndex(s => s.id === newSub.id) + 1;
              if (newIndex > 0) {
                setSelectedTab(newIndex);
              }
            }}
          />
          <AddOrEditSubCompanyDialog
            open={editDialogOpen}
            onClose={() => setEditDialogOpen(false)}
            initialData={editInitialData || undefined}
            mode='edit'
          />

        </CompanyManagementMainContentStyles>
      </>
    );
  }
  return <Box>{error}</Box>;
};

export default CompanyManagement;
